import { catchError, forkJoin, map, Observable, of } from "rxjs";

import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { Resource } from "./resource.type";
import { Translation, TranslocoLoader } from "@jsverse/transloco";

@Injectable({ providedIn: "root" })
export class TranslocoHttpLoader implements TranslocoLoader {
    /** Mirrors `fallbackLang` in the Transloco config: the language every scope is guaranteed to have. */
    private static readonly FALLBACK_LANG = "en";

    private _httpClient = inject(HttpClient);
    private readonly _assetVersion = this.getAssetVersion();

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get translation
     *
     * @param lang
     */
    getTranslation(lang: string): Observable<Translation> {
        const resources: Resource[] = [
            { prefix: "i18n/", suffix: ".json" },
            { prefix: "i18n/devextreme/", suffix: ".json" },
            { prefix: "i18n/breadcrumb/", suffix: ".json" },
            { prefix: "i18n/auth/sign-in/", suffix: ".json" },
            { prefix: "i18n/auth/password-reset/", suffix: ".json" },

            //System
            { prefix: "i18n/systems/account-group-system/", suffix: ".json" },
            { prefix: "i18n/systems/account-system/", suffix: ".json" },
            { prefix: "i18n/systems/role-permission/", suffix: ".json" },
            { prefix: "i18n/systems/selling-price-service/", suffix: ".json" },
            { prefix: "i18n/systems/topup-system/", suffix: ".json" },
            { prefix: "i18n/systems/currency-system/", suffix: ".json" },
            { prefix: "i18n/systems/country-system/", suffix: ".json" },
            { prefix: "i18n/systems/goods-type-system/", suffix: ".json" },
            // { prefix: "i18n/systems/conversion-system/", suffix: ".json" },
            { prefix: "i18n/systems/system-setting/", suffix: ".json" },
            { prefix: "i18n/systems/version/", suffix: ".json" },
            { prefix: "i18n/systems/measurement-conversion/", suffix: ".json" },
            { prefix: "i18n/systems/instruction-manual/", suffix: ".json" },
            
            { prefix: "i18n/systems/trade-document-template/", suffix: ".json" },
            { prefix: "i18n/systems/wallet-system/", suffix: ".json" },
            { prefix: "i18n/systems/financial-system/", suffix: ".json" },
            { prefix: "i18n/systems/payment-method/", suffix: ".json" },
            { prefix: "i18n/systems/voucher-system/", suffix: ".json" },


            //Management
            { prefix: "i18n/management/core-logistics-warehouse-address-management/", suffix: ".json" },
            { prefix: "i18n/management/vendor-management/", suffix: ".json" },
            { prefix: "i18n/management/core-logistics-service-management/", suffix: ".json" },
            { prefix: "i18n/management/shipment-management/", suffix: ".json" },
            { prefix: "i18n/shipment-result-detail/", suffix: ".json" },
            { prefix: "i18n/management/commission-management/", suffix: ".json" },
            // Kept separate because shared-assets also contains a legacy commission-management file
            // that is copied later by the Angular asset pipeline and would otherwise overwrite these keys.
            { prefix: "i18n/management/commission-event-operations/", suffix: ".json" },
            // SL-536: cùng lý do trên. Khoá commission-reconciliation.* trước đây nằm trong
            // management/commission-management của app, nhưng libs/shared-assets cũng có file
            // commission-management (bản legacy, KHÔNG có nhóm khoá này) và được asset pipeline copy SAU
            // nên ghi đè hẳn file của app trong dist ⇒ trang Đối soát hoa hồng hiện nguyên khoá kỹ thuật
            // ở MỌI ngôn ngữ. Tách sang thư mục riêng (URL riêng) thì không còn bị ghi đè.
            { prefix: "i18n/management/commission-reconciliation/", suffix: ".json" },
            // Đối soát tài chính theo shipment — scope riêng để không bị dictionary dùng chung ghi đè.
            { prefix: "i18n/management/financial-reconciliation/", suffix: ".json" },
            { prefix: "i18n/management/customer-management/", suffix: ".json" },
            { prefix: "i18n/management/platform-customer/", suffix: ".json" },
            { prefix: "i18n/platform/", suffix: ".json" },
            { prefix: "i18n/content/tenant-site/", suffix: ".json" },
            { prefix: "i18n/management/network-workspace/", suffix: ".json" },
            { prefix: "i18n/dashboards/multi-level-dashboard/", suffix: ".json" },
            { prefix: "i18n/dashboards/home/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-report/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-info/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-creation-progress/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-import/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-creation/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-create/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-reconciliation/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-adjustment/", suffix: ".json" },
            { prefix: "i18n/shipment/receivable-management/", suffix: ".json" },
            { prefix: "i18n/shipment/tracking-label-management/", suffix: ".json" },
            { prefix: "i18n/shipment/shipment-quotation/", suffix: ".json" },
            { prefix: "i18n/shipment/tracking-event/", suffix: ".json" },
            { prefix: "i18n/shipment/tracking-automation/", suffix: ".json" },
            // Luồng chọn nhà cung cấp + các bộ chọn phạm vi dùng chung (nhóm khách hàng / dịch vụ tenant).
            // Scope riêng để không ghi đè bất kỳ dictionary dùng chung nào (xem ghi chú ở khối i18n-app).
            { prefix: "i18n/shipment/vendor-selection-audit/", suffix: ".json" },
            { prefix: "i18n/systems/notification/", suffix: ".json" },
            { prefix: "i18n/services/price-book-studio/", suffix: ".json" },
            { prefix: "i18n/services/pricing-workspace/", suffix: ".json" },
            { prefix: "i18n/services/routing-admin/", suffix: ".json" },
            { prefix: "i18n/services/fulfillment-plan/", suffix: ".json" },
            { prefix: "i18n/services/vendor-integration/", suffix: ".json" },
            { prefix: "i18n/services/core-logistics-service-management/", suffix: ".json" },
            { prefix: "i18n/dispatch/", suffix: ".json" },

            // VLP app overrides, published under /i18n-app and merged AFTER their shared counterparts.
            //
            // Careful — this is a merge of two SEPARATE URLs, not of two files at the same path: the app's
            // own `public/` folder is published at the root as well, so an app dictionary whose relative
            // path matches a shared one REPLACES that shared file wholesale at /i18n/... An app-side
            // dictionary therefore has to be a full superset of the shared one, or not exist at all.
            // systems/wallet-system was the counter-example: an 80-key app file shadowed the 214-key shared
            // dictionary, so the whole wallet workspace rendered raw keys (the app file is now merged into
            // shared-assets and deleted — do not re-add it).
            { prefix: "i18n-app/management/commission-management/", suffix: ".json" },
            { prefix: "i18n-app/shipment/shipment-reconciliation/", suffix: ".json" },
            { prefix: "i18n-app/systems/financial-system/", suffix: ".json" },
            { prefix: "i18n-app/quality/", suffix: ".json" },
            { prefix: "i18n-app/work/", suffix: ".json" },

            //Services

            //{ prefix: "i18n/services/vendor-management/", suffix: ".json" },

            // Reporting upgrade Phase 3-5 — reporting module labels.
            { prefix: "i18n/reporting/", suffix: ".json" },
            // SL-352 — global address editor / validation
            { prefix: "i18n/address-validation/", suffix: ".json" },

        ];

        const ret = forkJoin(
            resources.map((config: Resource) => this.loadResource(config, lang)),
        ).pipe(map((response: Translation[]) => this.mergeObjectsRecursively(response)));

        return ret;
    }

    /**
     * Loads one scope file, degrading instead of failing.
     *
     * `forkJoin` rejects as soon as ANY source errors, so a single scope that has no file for the
     * requested language used to take down the WHOLE bundle — the app then rendered raw keys
     * everywhere. That is exactly what happened to Deutsch, which had no
     * `i18n/services/pricing-workspace/de.json`.
     *
     * A missing scope now falls back to the default-language file for that same scope, so an
     * untranslated screen shows English text rather than keys, and an entirely absent scope resolves
     * to `{}` instead of breaking every other screen.
     */
    private loadResource(config: Resource, lang: string): Observable<Translation> {
        return this.fetchResource(config, lang).pipe(
            catchError(() =>
                lang === TranslocoHttpLoader.FALLBACK_LANG
                    ? of({} as Translation)
                    : this.fetchResource(config, TranslocoHttpLoader.FALLBACK_LANG).pipe(
                        catchError(() => of({} as Translation)),
                    ),
            ),
        );
    }

    private fetchResource(config: Resource, lang: string): Observable<Translation> {
        return this._httpClient.get<Translation>(
            `${config.prefix}${lang}${config.suffix}?v=${encodeURIComponent(this._assetVersion)}`,
        );
    }

    mergeObjectsRecursively(objects: Translation[]): Translation {
        const mergedObject: Translation = {};

        for (const obj of objects) {
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    if (typeof obj[key] === "object" && obj[key] !== null) {
                        mergedObject[key] = this.mergeObjectsRecursively([mergedObject[key], obj[key]]);
                    } else {
                        mergedObject[key] = obj[key];
                    }
                }
            }
        }
        return mergedObject;
    }

    /** Reuse translations within one app load, but never reuse JSON cached by an earlier load. */
    private getAssetVersion(): string {
        const mainScript = Array.from(document.scripts)
            .map((script) => script.src)
            .find((src) => /main-[^/]+\.js(?:\?|$)/.test(src));
        const bundleVersion = mainScript?.match(/main-([^/.]+)\.js/)?.[1] ?? "development";

        return `${bundleVersion}-${Date.now().toString(36)}`;
    }
}
